/** An independently owned chooser; never moves the board's single map container. */
import { useEffect, useRef, useState } from 'react';
import type * as MapGL from '@2gis/mapgl/types';
import type { Map as MapGLMap, Marker } from '@2gis/mapgl/types';
import { toLngLat } from '../map/objects';
import { agentMapglKey, loadAgentMapgl } from './agentMapgl';
import { PARK_CENTER } from './objects';

type Point = { lat: number; lon: number };
export default function PickerMap({ point, onPick }: { point: Point | null; onPick: (point: Point) => void }) {
  const slot = useRef<HTMLDivElement>(null);
  const select = useRef(onPick); select.current = onPick;
  const instance = useRef<MapGLMap | null>(null);
  const marker = useRef<Marker | null>(null);
  const sdkRef = useRef<typeof MapGL | null>(null);
  const [ready, setReady] = useState(false);
  const [fallback, setFallback] = useState(false);
  const key = agentMapglKey();
  useEffect(() => {
    if (fallback) return;
    let cancelled = false;
    let map: MapGLMap | null = null;
    if (!key) {
      setFallback(true);
      return () => { cancelled = true; };
    }
    void loadAgentMapgl().then((sdk) => {
      if (cancelled) return;
      if (!sdk || !slot.current) { setFallback(true); return; }
      try {
        map = new sdk.Map(slot.current, { key, center: toLngLat(point ?? PARK_CENTER), zoom: 15,
          graphicsPreset: 'light', disableRotationByUserInteraction: true, disablePitchByUserInteraction: true });
        map.setLanguage('ru');
        instance.current = map; sdkRef.current = sdk;
        map.on('click', (e) => {
          const [lon, lat] = e.lngLat;
          if (lat !== undefined && lon !== undefined) select.current({ lat, lon });
        });
        map.on('error', (e) => { if (['styleloaderror', 'webglcontextlost', 'invalidtilekey'].includes(e.type)) setFallback(true); });
        if (point) marker.current = new sdk.Marker(map, { coordinates: toLngLat(point) });
        setReady(true);
      } catch { setFallback(true); }
    }).catch(() => { if (!cancelled) setFallback(true); });
    return () => { cancelled = true; marker.current?.destroy(); marker.current = null; map?.destroy(); instance.current = null; sdkRef.current = null; };
    // Initial centre only; later selections update the marker without rebuilding tiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback, key]);
  useEffect(() => {
    if (!point) { marker.current?.destroy(); marker.current = null; return; }
    if (!ready || !instance.current || !sdkRef.current) return;
    if (!marker.current) marker.current = new sdkRef.current.Marker(instance.current, { coordinates: toLngLat(point) });
    else marker.current.setCoordinates(toLngLat(point));
    instance.current.setCenter(toLngLat(point));
  }, [point, ready]);
  return <>
    {!fallback ? <div ref={slot} className="location-map" aria-label="Карта выбора местоположения" /> :
      <p role="status">Карта недоступна. Выберите объект или введите координаты ниже.</p>}
    {!fallback && <p className="dev-muted">Нажмите на карту, чтобы выбрать точку. Маркер на карте показывает выбор.</p>}
  </>;
}
